import { invariant } from '@ifixit/helpers';
import { type RefObject, createContext, use } from 'react';
import type {
   DropdownMenuItem,
   DropdownMenuRadioGroup,
   DropdownMenuRadioItem,
} from '../components/dropdown-menu-eager';

/** Where focus lands on opening: the menu itself after a pointer press, so no
 * item lights up under the pointer, or the first or last item from a key. */
export type OpenFocus = 'menu' | 'first' | 'last';

export interface DropdownMenuContextValue {
   open: boolean;
   menuId: string;
   triggerId: string;
   openWith: (focus: OpenFocus) => void;
   close: (options: { returnFocus: boolean }) => void;
}

/** What event handlers and effects share but rendering never reads. */
export interface DropdownMenuInternals {
   trigger: HTMLElement | null;
   menu: HTMLDivElement | null;
   openFocus: OpenFocus;
   search: string;
   searchTimer: number;
}

export const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);
export const InternalsContext = createContext<RefObject<DropdownMenuInternals> | null>(null);

/**
 * The items of an open menu, from whichever content renders them. Items
 * imported from the lazy entry read them here, so they also work inside
 * content imported from the eager one.
 */
export const OpenMenuItemsContext = createContext<{
   Item: typeof DropdownMenuItem;
   RadioGroup: typeof DropdownMenuRadioGroup;
   RadioItem: typeof DropdownMenuRadioItem;
} | null>(null);

export const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"]';

export function useDropdownMenu() {
   const context = use(DropdownMenuContext);
   invariant(context, 'DropdownMenu parts must be rendered inside a DropdownMenu');
   return context;
}

export function useInternals() {
   const internalsRef = use(InternalsContext);
   invariant(internalsRef, 'DropdownMenu parts must be rendered inside a DropdownMenu');
   return internalsRef;
}

export function menuItems(menu: HTMLElement | null) {
   return Array.from(menu?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
}

export function focusElement(element: HTMLElement | null | undefined) {
   element?.focus({ preventScroll: true });
   element?.scrollIntoView?.({ block: 'nearest' });
}

export function focusInitial({ menu, openFocus }: DropdownMenuInternals) {
   if (openFocus === 'menu') menu?.focus({ preventScroll: true });
   else focusElement(openFocus === 'first' ? menuItems(menu)[0] : menuItems(menu).at(-1));
}

/**
 * A press or focus outside the menu and its trigger closes the menu and
 * leaves focus where the person put it. Escape closes only the menu:
 * dialogs, drawers and sheets listen for it on the document, and the window
 * sees it first.
 */
export function listenForDismiss(
   internals: DropdownMenuInternals,
   close: DropdownMenuContextValue['close']
) {
   function isOutside(target: EventTarget | null) {
      return (
         target instanceof Node &&
         !internals.menu?.contains(target) &&
         !internals.trigger?.contains(target)
      );
   }
   function handlePointerDown(event: PointerEvent) {
      if (isOutside(event.target)) close({ returnFocus: false });
   }
   function handleFocusIn(event: FocusEvent) {
      if (isOutside(event.target)) close({ returnFocus: false });
   }
   function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close({ returnFocus: true });
   }
   document.addEventListener('pointerdown', handlePointerDown, { capture: true });
   document.addEventListener('focusin', handleFocusIn);
   window.addEventListener('keydown', handleKeyDown, { capture: true });
   return () => {
      document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      document.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
   };
}
